import type { Provider } from '@angular/core';
import type { IUserShort } from '@dev-fast/types';
import { WalletTypeEnum } from '@dev-fast/types';
import isNil from 'lodash-es/isNil';
import { DateTime } from 'luxon';

import type {
  IItemWithBaseItem,
  IItemWithFreeCases,
  IQuest,
  IQuestCalendar,
  IQuestDescription,
  IRaffle,
  IRaffleConditionRaffle,
  IRaffleItem,
  IRafflesData,
  IResponseRaffleItem,
  IResponseReward,
  IUserRaffle,
} from '@app/core/quest';
import { EQuestStatus, ERaffleStatus, ERaffleType, QUEST_ADAPTER, QUEST_ID } from '@app/core/quest';
import { getStaticUrl } from '@app/shared/utils/static-url';

/**
 * РљР»СЋС‡ Р»РѕРєР°Р»РёР·Р°С†РёРё РґР»СЏ Р·Р°РґР°РЅРёСЏ РїРѕ СѓРјРѕР»С‡Р°РЅРёСЋ, РєРѕРіРґР° С‚РёРї РёРіСЂС‹ РЅРµ РЅР°Р№РґРµРЅ РІ СЃР»РѕРІР°СЂРµ
 */
const FREE_QUEST_LOCALIZATION_KEY = 'EVENT.ICE_BREAKERS.QUEST.QUEST_DEFAULT';

/**
 * РџСЂРµРѕР±СЂР°Р·СѓРµС‚ РґР°РЅРЅС‹Рµ РёР· С„РѕСЂРјР°С‚Р° IQuestCalendar РІ РјР°СЃСЃРёРІ IQuest[]
 */
export function mapQuestCalendarToAprilMadnessQuests(questCalendar: IQuestCalendar[]): IQuest[] {
  return questCalendar.map((cellQuest) => {
    const startAtTimestamp = DateTime.fromISO(cellQuest.startAt).toMillis();
    const finishAtTimestamp = DateTime.fromISO(cellQuest.finishAt).toMillis();
    const [firstQuest] = cellQuest.questCalendarCellsQuests;

    const task = firstQuest?.quest;
    const params = task?.params || { count: 1, amount: 0 };
    const gameTypeId = task?.gameTypeId;
    const progress = task?.userProgress?.progress || 0;
    const reward = firstQuest?.userQuestCalendar?.finishAt ? firstQuest?.userQuestCalendar.rewardValue : firstQuest?.reward.userValue;
    const description = APRIL_MADNESS_QUEST_LOCALIZATION_KEYS[cellQuest.title] ?? FREE_QUEST_LOCALIZATION_KEY;

    const getStatus = (): EQuestStatus => {
      const currentTimestamp = DateTime.now().toMillis();

      if (firstQuest?.userQuestCalendar?.finishAt) {
        return EQuestStatus.REWARDED;
      }
      if (finishAtTimestamp < currentTimestamp) {
        return EQuestStatus.EVENT_COMPLETED;
      }

      if (startAtTimestamp > currentTimestamp) {
        return EQuestStatus.UNAVAILABLE;
      }

      if (progress >= (isNil(params.count) ? params.amount : params.count) || (!task && !isNil(firstQuest.userQuestCalendar?.isActive))) {
        return EQuestStatus.COMPLETED;
      }
      if (firstQuest.userQuestCalendar?.isActive) {
        return EQuestStatus.ACTIVE;
      }
      if (startAtTimestamp <= currentTimestamp) {
        return EQuestStatus.AVAILABLE;
      }

      return EQuestStatus.FAILED;
    };

    // РЎРѕР·РґР°РµРј РѕРїРёСЃР°РЅРёРµ РґР»СЏ РјРѕРґР°Р»СЊРЅРѕРіРѕ РѕРєРЅР°
    const eventDescription: IQuestDescription[] = cellQuest.questCalendarCellInfo.map((info) => ({
      text: info.description || '',
      image: info.image || '',
      title: cellQuest.title,
    }));

    return {
      id: cellQuest.id,
      title: cellQuest.title || 'EVENT.ICE_BREAKERS.QUEST.DEFAULT_TITLE',
      eventDescription,
      task: {
        questId: firstQuest?.quest?.id || 0,
        reward: Math.round(reward / 100),
        params,
        gameTypeId,
        progress,
        description,
      },
      status: getStatus(),
      isActive: !!firstQuest.userQuestCalendar?.isActive,
      startAt: startAtTimestamp,
      finishAt: finishAtTimestamp,
    };
  });
}

/**
 * РЎР»РѕРІР°СЂСЊ СЃРѕРѕС‚РІРµС‚СЃС‚РІРёСЏ С‚РёРїРѕРІ РёРіСЂ Рё РєР»СЋС‡РµР№ РґР»СЏ Р»РѕРєР°Р»РёР·Р°С†РёРё Р·Р°РґР°РЅРёР№
 */
export const APRIL_MADNESS_QUEST_LOCALIZATION_KEYS: Record<string, string> = {
  'EVENT.ICE_BREAKERS.QUEST.QUEST_1_REFILL': 'EVENT.ICE_BREAKERS.QUEST.QUEST_1_REFILL',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_1': 'EVENT.ICE_BREAKERS.QUEST.QUEST_2',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_2': 'EVENT.ICE_BREAKERS.QUEST.QUEST_3',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_3': 'EVENT.ICE_BREAKERS.QUEST.QUEST_4',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_4': 'EVENT.ICE_BREAKERS.QUEST.QUEST_5',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_5': 'EVENT.ICE_BREAKERS.QUEST.QUEST_6',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_6': 'EVENT.ICE_BREAKERS.QUEST.QUEST_7',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_7': 'EVENT.ICE_BREAKERS.QUEST.QUEST_8',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_8': 'EVENT.ICE_BREAKERS.QUEST.QUEST_9',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_9': 'EVENT.ICE_BREAKERS.QUEST.QUEST_10',
  'EVENT.ICE_BREAKERS.TASKS.EVENT_10': 'EVENT.ICE_BREAKERS.QUEST.QUEST_11',
};

/**
 * РџСЂРµРѕР±СЂР°Р·СѓРµС‚ РґР°РЅРЅС‹Рµ РёР· С„РѕСЂРјР°С‚Р° IRafflesData РІ РјР°СЃСЃРёРІ IRaffle[]
 */
export function mapRafflesDataToAprilMadnessRaffles(rafflesData: IRafflesData): IRaffle[] {
  if (!rafflesData || !rafflesData.rafflesQuests) {
    return [];
  }

  return rafflesData.rafflesQuests.map((raffleQuest) => {
    const { raffle } = raffleQuest;
    // Р‘РµСЂРµРј РїРµСЂРІСѓСЋ РЅР°РіСЂР°РґСѓ РґР»СЏ РѕРїСЂРµРґРµР»РµРЅРёСЏ РёРЅС„РѕСЂРјР°С†РёРё Рѕ РїСЂРёР·Рµ
    const [firstReward] = raffle.raffleRewardsRaffle || [];
    const rewardItem = firstReward?.reward;

    return {
      id: raffle.id,
      userCount: raffle.countParticipants,
      maxUserCount: raffle.limitParticipants,
      raffleTimeAt: DateTime.fromISO(raffle.raffleTimeAt).toMillis(),
      participants: aprilMadnessParticipantsFactory(raffle.usersRaffle),
      isUserParticipant: raffleQuest.raffle.isUserParticipant,
      participationPrice: getParticipationPrice(raffle.raffleConditionsRaffle),
      item: extractRewardItemData(rewardItem, raffle.link),
      status: determineRaffleStatus(raffle),
      winner: raffle.raffleFinish ? getWinner(raffle) : undefined,
    };
  });
}

/**
 * РћРїСЂРµРґРµР»СЏРµС‚ СЃС‚РѕРёРјРѕСЃС‚СЊ СѓС‡Р°СЃС‚РёСЏ РІ СЂРѕР·С‹РіСЂС‹С€Рµ РЅР° РѕСЃРЅРѕРІРµ СѓСЃР»РѕРІРёР№ СЂРѕР·С‹РіСЂС‹С€Р°
 */
export function getParticipationPrice(raffleConditions: IRaffleConditionRaffle[] | undefined): number {
  if (!raffleConditions || !raffleConditions.length) {
    return 0;
  }
  const refillCondition = raffleConditions.find(
    (condition) => condition.condition?.actionType === 'refill' && condition.condition?.itemId === WalletTypeEnum.BALLOONS,
  );

  return Math.round((refillCondition?.condition?.value ?? 0) / 100);
}

/**
 * РћРїСЂРµРґРµР»СЏРµС‚ СЃС‚Р°С‚СѓСЃ СЂРѕР·С‹РіСЂС‹С€Р° РЅР° РѕСЃРЅРѕРІРµ РґР°РЅРЅС‹С… СЃ СЃРµСЂРІРµСЂР°
 */
function determineRaffleStatus(raffle: IResponseRaffleItem): ERaffleStatus {
  const now = DateTime.now().toMillis();
  const registrationStartAt = DateTime.fromISO(raffle.registrationStartAt).toMillis();
  const registrationEndAt = DateTime.fromISO(raffle.registrationEndAt).toMillis();
  const raffleTimeAt = DateTime.fromISO(raffle.raffleTimeAt).toMillis();

  if (raffle.raffleFinish) {
    return ERaffleStatus.WON;
  }

  if (now >= registrationStartAt && now <= registrationEndAt) {
    return ERaffleStatus.FILLING;
  }

  if (now > registrationEndAt && now <= raffleTimeAt) {
    return ERaffleStatus.WAITING;
  }
  return ERaffleStatus.FULL_FILLED;
}

/**
 * РџРѕР»СѓС‡Р°РµС‚ РёРЅС„РѕСЂРјР°С†РёСЋ Рѕ РїРѕР±РµРґРёС‚РµР»Рµ СЂРѕР·С‹РіСЂС‹С€Р°
 */
function getWinner(raffle: IResponseRaffleItem): IUserShort | undefined {
  const [winner] = raffle.raffleRewardsRaffle?.[0]?.winners || [];
  return winner?.user;
}

/**
 * РР·РІР»РµРєР°РµС‚ РґР°РЅРЅС‹Рµ Рѕ РЅР°РіСЂР°РґРµ РёР· РѕР±СЉРµРєС‚Р° rewardItem
 */
function extractRewardItemData(rewardItem: IResponseReward, link: string): IRaffleItem {
  const isItemWithBaseItem = (item: unknown): item is IItemWithBaseItem => {
    return !!item && typeof item === 'object' && 'baseItem' in item;
  };

  const isItemWithFreeCases = (item: unknown): item is IItemWithFreeCases => {
    return !!item && typeof item === 'object' && 'freeCases' in item;
  };

  const defaultData = {
    id: 0,
    image: '',
    name: 'Unknown',
    rarity: '',
    price: 0,
    type: 'Unknown',
    typeId: ERaffleType.SKIN,
  };

  if (!rewardItem?.item) {
    return defaultData;
  }

  const item = rewardItem.item;

  if (isItemWithBaseItem(item)) {
    return {
      id: rewardItem?.id || 0,
      image: item.baseItem?.icon || defaultData.image,
      name: item.baseItem?.skin || defaultData.name,
      rarity: item.baseItem?.rarity || defaultData.rarity,
      price: item.price || defaultData.price,
      type: item.baseItem?.shortName || defaultData.type,
      typeId: ERaffleType.SKIN,
    };
  } else if (isItemWithFreeCases(item)) {
    const firstCase = item.freeCases?.[0]?.case;
    return {
      id: rewardItem?.id || 0,
      image: firstCase?.itemBackgroundImage || defaultData.image,
      name: firstCase?.name || defaultData.name,
      rarity: firstCase?.mainItem?.rarity || defaultData.rarity,
      price: firstCase?.mainItemInventory?.price || defaultData.price,
      type: 'CASES.ANOTHER_CASES.CASE',
      typeId: ERaffleType.FREE_CASE,
      case: firstCase,
    };
  }

  return defaultData;
}

export const createAprilMadnessUserFallBack = (user: Partial<IUserShort>): IUserShort => {
  return {
    id: user.id || 0,
    name: user.name || 'Unknown',
    avatar: user.avatar || getStaticUrl('/common/fallback_avatar.webp'),
  };
};

export const aprilMadnessParticipantsFactory = (usersRaffle: IUserRaffle[] | null | undefined): IUserShort[] | undefined => {
  if (!usersRaffle || usersRaffle.length === 0) {
    return undefined;
  }

  return usersRaffle.map((participant: IUserRaffle) => participant.user || createAprilMadnessUserFallBack({ id: participant.userId }));
};

// Ice Breakers РёСЃРїРѕР»СЊР·СѓРµС‚ QUEST_ID = 3 (РїСЂРµРґРїРѕР»РѕР¶РёС‚РµР»СЊРЅРѕ, РЅСѓР¶РЅРѕ СѓС‚РѕС‡РЅРёС‚СЊ)
export const APRIL_MADNESS_QUEST_ID = 2;

export function provideAprilMadnessQuest(): Provider {
  return {
    provide: QUEST_ID,
    useValue: APRIL_MADNESS_QUEST_ID,
  };
}

export function provideAprilMadnessAdapter(): Provider {
  return {
    provide: QUEST_ADAPTER,
    useValue: {
      mapQuestCalendarToQuests: mapQuestCalendarToAprilMadnessQuests,
      mapRafflesDataToRaffles: mapRafflesDataToAprilMadnessRaffles,
      createUserFallBack: createAprilMadnessUserFallBack,
      participantsFactory: aprilMadnessParticipantsFactory,
    },
  };
}
